<script setup lang="ts">
/**
 * DetailSidebar —— 详情页左侧目录导航（F10，对齐模板 `.sidebar`）。
 *
 * 渲染 `Section.sidebar: { name, sectionId, anchor? }[]`（B19）。item 点击
 * `router.push('/detail/<title-slug>#<anchor>')`；anchor 空则跳目标详情页顶部。
 *
 * **目标 sectionId 不在 Store**：该 item 置灰 + `console.error` + 点击不跳转
 * （Debug-First 暴露悬空 sidebar 引用，不静默吞咽）。其他 item 不受影响。
 *
 * **不读 store**：详情页 sections 快照由父（DetailPage）透传 `sections` prop，
 * 保持组件可独立 mount 测试。预览 token 同样透传，预览态跳转携 `?preview=`。
 *
 * **active 状态**：当前路由 `sectionId` + hash 精确匹配的 item 高亮
 * （对齐模板 `.sidebar ul li a.active` 左竖条 + padding-left）。同一
 * sectionId 不同 anchor 不共享高亮，避免「成果简介 / 支撑材料」同时
 * 出现竖线。
 *
 * 空数组 → 整个 sidebar 区不渲染（F10 What to build #6）。
 */
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import {
  detailRouteLocation,
  detailPathFor,
  findSectionByDetailKey,
} from '@/composables/detail-path';
import { copy } from '@/i18n/zh-CN';
import type { PublicSectionView, SidebarItemView } from '@/api/types';

/**
 * 目标缺失时的占位 Section：仅填 `id` + 空 `title`，供 `detailRouteLocation`
 * 生成 `/detail/<missing-id>` 路径，让 DetailPage 兜 Detail-level Fallback。
 * 其余字段非跳转路径所需，填零值。
 */
const EMPTY_SECTION: PublicSectionView = {
  id: '',
  type: '',
  title: '',
  summary: '',
  bodyHtml: '',
  cover: null,
  videoUrl: null,
  posterUrl: null,
  images: [],
  materials: [],
  sidebar: [],
  sort: 0,
  publishStatus: 'Draft',
  createdAt: '',
  updatedAt: '',
};

const props = defineProps<{
  readonly items: readonly SidebarItemView[];
  readonly sections: readonly PublicSectionView[];
  readonly previewToken?: string | null;
}>();

const route = useRoute();
const router = useRouter();

const visible = computed(() => props.items.length > 0);

interface ResolvedItem {
  readonly item: SidebarItemView;
  readonly target: PublicSectionView | undefined;
}

const resolved = computed<readonly ResolvedItem[]>(() =>
  props.items.map((item) => ({
    item,
    target: props.sections.find((section) => section.id === item.sectionId),
  })),
);
const currentSectionId = computed(() => {
  const routeKey = route.params.sectionId;
  if (typeof routeKey !== 'string') {
    return null;
  }
  return findSectionByDetailKey(props.sections, routeKey)?.id ?? routeKey;
});

function isActive(item: SidebarItemView): boolean {
  if (currentSectionId.value !== item.sectionId) {
    return false;
  }
  const itemHash = item.anchor ? `#${item.anchor}` : '';
  return route.hash === itemHash;
}

/**
 * 点击解析目标 + 跳转。目标在 Store → `router.push` 到目标详情页（带 anchor）；
 * 目标缺失 → 仍 `router.push('/detail/<missing-id>')`，让 DetailPage 兜
 * Detail-level Fallback「内容不存在」（issue #6 字面：目标 sectionId 不在
 * Store 时进入 Fallback，而非在 sidebar 侧静默置灰）。sidebar 侧同时
 * `console.error` 保留悬空引用信号（Debug-First 不抹错）。
 */
function handleClick(event: MouseEvent, entry: ResolvedItem): void {
  event.preventDefault();
  const { item, target } = entry;
  if (target === undefined) {
    // eslint-disable-next-line no-console
    console.error(
      `[DetailSidebar] sidebar item "${item.name}" references missing section "${item.sectionId}"`,
    );
    void router.push(
      detailRouteLocation(
        { ...EMPTY_SECTION, id: item.sectionId, title: '' },
        { previewToken: props.previewToken },
      ),
    );
    return;
  }
  const location = detailRouteLocation(target, {
    anchor: item.anchor,
    previewToken: props.previewToken,
  });
  if (currentSectionId.value === item.sectionId) {
    void router.replace(location);
    return;
  }
  void router.push(location);
}

/** href 用真实详情页路径（目标缺失时仍指向 /detail/<id>），支持中键 / 右键打开。 */
function hrefFor(entry: ResolvedItem): string {
  const { item, target } = entry;
  const section = target ?? { ...EMPTY_SECTION, id: item.sectionId, title: '' };
  return detailPathFor(section, item.anchor);
}
</script>

<template>
  <aside v-if="visible" :class="$style.sidebar" data-testid="detail-sidebar">
    <div :class="$style.title" data-shell="sidebar-title">
      {{ copy.detail.sidebarTitle }}
    </div>
    <ul :class="$style.list">
      <li v-for="entry in resolved" :key="`${entry.item.sectionId}-${entry.item.anchor ?? ''}`">
        <a
          :class="[
            $style.link,
            isActive(entry.item) ? $style.active : null,
            entry.target === undefined ? $style.disabled : null,
          ]"
          :href="hrefFor(entry)"
          :aria-disabled="entry.target === undefined ? 'true' : undefined"
          :data-active="isActive(entry.item) ? 'true' : undefined"
          :data-testid="`sidebar-item-${entry.item.sectionId}`"
          @click="handleClick($event, entry)"
        >
          {{ entry.item.name }}
        </a>
      </li>
    </ul>
  </aside>
</template>

<style module>
.sidebar {
  width: var(--detail-sidebar-width);
  flex: 0 0 var(--detail-sidebar-width);
  background: var(--color-bg-primary);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
  overflow: hidden;
  position: sticky;
  top: var(--space-3);
}

.title {
  background: var(--color-brand-primary);
  color: var(--color-bg-primary);
  padding: var(--space-2) var(--space-3);
  font-size: var(--font-size-md);
  font-weight: 700;
}

.list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.link {
  display: block;
  position: relative;
  padding: var(--space-2) var(--space-3);
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text-secondary);
  font-size: var(--font-size-base);
  transition: padding-left var(--template-hover-duration) ease,
    color var(--template-hover-duration) ease,
    background var(--template-hover-duration) ease;
}

.link::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 4px;
  background: transparent;
  transition: background var(--template-hover-duration) ease;
}

.link:hover {
  padding-left: calc(var(--space-3) + var(--space-1));
  color: var(--color-brand-primary);
  background: var(--color-bg-secondary);
}

.link:hover::before,
.active::before {
  background: var(--color-brand-primary);
}

.active {
  color: var(--color-brand-primary);
  background: var(--color-bg-secondary);
  padding-left: calc(var(--space-3) + var(--space-1));
}

.disabled {
  color: var(--color-text-muted);
  cursor: not-allowed;
}

.disabled:hover {
  padding-left: var(--space-3);
  color: var(--color-text-muted);
  background: transparent;
}

.disabled:hover::before {
  background: transparent;
}

@media (max-width: 959px) {
  .sidebar {
    width: 100%;
    flex-basis: auto;
    position: static;
  }
}
</style>
